import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ArrowLeft } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernProjects — the «Moderno» theme "Featured Projects" (portfolio), s140.
// Anthony approved TWO layouts and asked for a CMS switch between them
// (explicit exception to D-2026-126): Admin → Portfolio → "Section layout"
// stores `layout` on the aurex_portfolio_cfg doc — 'feature' (default,
// Feature + Grid) | 'rail' (Graphite Rail). Items = Portfolio manager; header
// + CTA = Section Manager → Portfolio — Section Configuration. Colours from
// theme tokens + graphite #16171a.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);
const plain = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
const cap = (s) => String(s || '').replace(/^\w/, c => c.toUpperCase());
const num = (i) => String(i + 1).padStart(2, '0');

function useMeta() {
  const tt = useT();
  return (p) => {
    const cat = tt(p.category);
    const tags = Array.isArray(p.tags) ? p.tags.map(t => cap(tt(t))).filter(Boolean) : [];
    return [cat, ...tags].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 3).join(' · ');
  };
}

function Cover({ src, className = '' }) {
  const [broken, setBroken] = React.useState(false);
  const url = img(src);
  if (!url || broken) return <span className={`block w-full h-full ${className}`} style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 35%, #fff), var(--color-accent, #ff9c1b))' }} />;
  return <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} className={`w-full h-full object-cover ${className}`} />;
}

// Wraps a project in its external link when it has one; otherwise a plain block.
function ProjectLink({ p, className, children, ...rest }) {
  if (!p.link) return <div className={className} {...rest}>{children}</div>;
  const blank = !!p.open_in_new_tab;
  return <a href={p.link} target={blank ? '_blank' : '_self'} rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
}

function Header({ cmsConfig, dark = false, extra = null }) {
  const tt = useT();
  const title = tt(cmsConfig.title) || 'Featured Projects';
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  const accent = dark ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)';
  const ctaUrl = tt(cmsConfig.cta_url) || '/featured-projects';
  const ctaText = tt(cmsConfig.cta_text) || 'View all projects';
  const external = /^https?:\/\//i.test(ctaUrl);
  const ctaProps = { className: `mx-link ${dark ? 'mx-dark ' : ''}inline-flex items-center gap-2 font-bold text-sm`, style: { color: accent }, 'data-testid': 'portfolio-view-all' };
  const target = cmsConfig.cta_new_tab ? '_blank' : '_self';
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 lg:mb-14">
      <div className="flex flex-col gap-4 max-w-2xl">
        {tt(cmsConfig.eyebrow) && (
          <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: accent }}>
            <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(cmsConfig.eyebrow)}
          </div>
        )}
        <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: dark ? '#fff' : 'var(--color-heading, #1a2332)' }} data-testid="portfolio-title">
          {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: accent }}>{last}</em></>}
        </h2>
        {tt(cmsConfig.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: dark ? 'rgba(255,255,255,.7)' : 'var(--color-body-text, #475569)' }}>{tt(cmsConfig.subtitle)}</p>}
      </div>
      <div className="flex items-center gap-6 self-start md:self-end">
        {extra}
        {external
          ? <a href={ctaUrl} target={target} rel="noopener noreferrer" {...ctaProps}>{ctaText} <ArrowRight className="w-4 h-4" /></a>
          : <Link to={ctaUrl} target={target} {...ctaProps}>{ctaText} <ArrowRight className="w-4 h-4" /></Link>}
      </div>
    </div>
  );
}

const RAIL_INSET = 'calc(max(0px, (100% - 1440px) / 2) + var(--g))';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28';
const line = 'rgba(20,26,58,.1)';

/* ── Feature + Grid (default) ─────────────────────────────────────────────────
   The first project as a wide split feature (image + case text); the next
   three as image cards with the caption below the image (never over it). */
function LeadVariant({ list, cmsConfig }) {
  const tt = useT();
  const meta = useMeta();
  const [lead, ...rest] = list;
  const more = rest.slice(0, 3);
  return (
    <div className={shell}>
      <Header cmsConfig={cmsConfig} />
      <ProjectLink p={lead} className="group grid gap-8 lg:gap-14 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] items-center" data-testid={`project-${lead.id}`}>
        <div className="aspect-[16/10] overflow-hidden rounded-[3px]"><Cover src={lead.image} className="transition-transform duration-500 ease-out group-hover:scale-[1.03]" /></div>
        <div className="flex flex-col gap-4">
          <span className="font-bold" style={{ fontFamily: SERIF, fontSize: '3rem', lineHeight: 1, color: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 55%, #fff)' }}>01</span>
          {meta(lead) && <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{meta(lead)}</span>}
          <h3 className="font-bold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: 'clamp(1.6rem, 2.4vw, 2.2rem)', lineHeight: 1.15, color: 'var(--color-heading, #1a2332)' }}>{tt(lead.title)}</h3>
          {plain(tt(lead.description)) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(tt(lead.description))}</p>}
          {lead.link && <span className="inline-flex items-center gap-2 font-bold text-sm mt-2" style={{ color: 'var(--accent-strong, #b86500)' }}>View project <ArrowUpRight className="w-4 h-4" /></span>}
        </div>
      </ProjectLink>
      {more.length > 0 && (
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3 mt-14 lg:mt-20 pt-10 border-t" style={{ borderColor: line }}>
          {more.map((p, i) => (
            <ProjectLink key={p.id} p={p} className="group flex flex-col gap-3" data-testid={`project-${p.id}`}>
              <div className="aspect-[4/3] overflow-hidden rounded-[3px] mb-2"><Cover src={p.image} className="transition-transform duration-500 ease-out group-hover:scale-[1.03]" /></div>
              <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{[num(i + 1), meta(p)].filter(Boolean).join(' · ')}</span>
              <span className="font-semibold inline-flex items-start justify-between gap-3 transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.3rem', lineHeight: 1.25, color: 'var(--color-heading, #1a2332)' }}>
                {tt(p.title)}{p.link && <ArrowUpRight className="w-4 h-4 flex-none mt-1.5" style={{ color: 'var(--accent-strong, #b86500)' }} />}
              </span>
              {plain(tt(p.description)) && <span className="text-[15px] leading-relaxed line-clamp-2" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(tt(p.description))}</span>}
            </ProjectLink>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Graphite Rail ──────────────────────────────────────────────────
   Graphite band with a horizontal scroll-snap rail of portrait cards
   (swipe on phones, arrows + counter on desktop). Caption below image. */
function RailVariant({ list, cmsConfig }) {
  const tt = useT();
  const meta = useMeta();
  const ref = React.useRef(null);
  const [pos, setPos] = React.useState({ at: 0, start: true, end: list.length <= 1 });
  const update = React.useCallback(() => {
    const el = ref.current; if (!el) return;
    const card = el.firstElementChild; const step = card ? card.getBoundingClientRect().width + 24 : 1;
    const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setPos({ at: end ? list.length - 1 : Math.min(list.length - 1, Math.round(el.scrollLeft / step)), start: el.scrollLeft < 4, end });
  }, [list.length]);
  React.useEffect(() => { update(); window.addEventListener('resize', update); return () => window.removeEventListener('resize', update); }, [update]);
  const go = (dir) => {
    const el = ref.current; if (!el) return;
    const card = el.firstElementChild; const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };
  const btn = 'w-11 h-11 rounded-full border flex items-center justify-center transition-[opacity,transform,background-color] duration-150 ease-out active:scale-[0.96] disabled:opacity-30 disabled:cursor-default hover:bg-white/10';
  const arrows = (
    <div className="hidden md:flex items-center gap-3">
      <span className="text-sm font-bold tabular-nums text-white/60 mr-2">{num(pos.at)} / {num(list.length - 1)}</span>
      <button type="button" aria-label="Previous project" onClick={() => go(-1)} disabled={pos.start} className={btn} style={{ borderColor: 'rgba(255,255,255,.25)', color: '#fff' }}><ArrowLeft className="w-4 h-4" /></button>
      <button type="button" aria-label="Next project" onClick={() => go(1)} disabled={pos.end} className={btn} style={{ borderColor: 'rgba(255,255,255,.25)', color: '#fff' }}><ArrowRight className="w-4 h-4" /></button>
    </div>
  );
  return (
    <div style={{ background: GRAPHITE }} className="pb-20 lg:pb-28">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] pt-20 lg:pt-28">
        <Header cmsConfig={cmsConfig} dark extra={arrows} />
      </div>
      {/* Rail bleeds to the right edge of the screen; the first card lines up
          with the header gutter (same inset as the 1440px shell). */}
      <div ref={ref} onScroll={update} className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-2 [--g:20px] sm:[--g:24px] lg:[--g:clamp(24px,5vw,80px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ paddingLeft: RAIL_INSET, scrollPaddingLeft: RAIL_INSET }}>
          {list.map((p, i) => (
            <ProjectLink key={p.id} p={p} className="group snap-start flex-none w-[78vw] sm:w-[340px] lg:w-[360px] flex flex-col gap-3" data-testid={`project-${p.id}`}>
              <div className="aspect-[4/5] overflow-hidden rounded-[3px] mb-2"><Cover src={p.image} className="transition-transform duration-500 ease-out group-hover:scale-[1.03]" /></div>
              <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--color-accent, #ff9c1b)' }}>{[num(i), meta(p)].filter(Boolean).join(' · ')}</span>
              <span className="font-semibold inline-flex items-start justify-between gap-3 text-white" style={{ fontFamily: SERIF, fontSize: '1.3rem', lineHeight: 1.25 }}>
                {tt(p.title)}{p.link && <ArrowUpRight className="w-4 h-4 flex-none mt-1.5 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" style={{ color: 'var(--color-accent, #ff9c1b)' }} />}
              </span>
              {plain(tt(p.description)) && <span className="text-[15px] leading-relaxed line-clamp-2 text-white/65">{plain(tt(p.description))}</span>}
            </ProjectLink>
          ))}
          <span aria-hidden="true" className="flex-none w-0" style={{ marginLeft: -24, paddingRight: 'var(--g)' }} />
      </div>
    </div>
  );
}

export default function ModernProjects({ items, cmsConfig = {} }) {
  const { lang } = useLang();
  const list = (items || []).filter(p => itemHasLocale(p.title, lang)).slice(0, 6);
  if (!list.length) return null;
  const V = cmsConfig.layout === 'rail' ? RailVariant : LeadVariant;
  return (
    <section id="portfolio" data-testid="portfolio-section" className="bg-white">
      <V list={list} cmsConfig={cmsConfig} />
    </section>
  );
}
